<template>
  <EInstantAnswer
    :sources="sources"
    :class="`weather--${state.toLowerCase()}`"
    :feedback-link="{
      ticketSubject: 'Search - Weather',
      widgetType: 'weather_rich'
    }"
    :show-footer="showFooter || isDesktopUp"
    class="weather"
    data-test-id="weather"
    powered-by-ecosia
  >
    <h2 class="sr-only">
      {{ $t('web.widgets.weather.title') }}
    </h2>
    <div :aria-busy="isLoading.toString()">
      <ELoading
        v-show="isLoading"
        class="weather__loading"
        data-test-id="weather-loading"
      />
      <template v-if="contentShown">
        <div class="weather__content" data-test-id="weather-content">
          <EDetails
            :location="location"
            :selected="selected"
            :units="units"
            v-bind="details"
            data-test-id="weather-details"
            @setUnits="setUnits"
          />
          <EForecast
            :daily-forecast="forecast"
            :hourly-forecast="hourlyForecast"
            :units="units"
            :is-mobile="!isTabletUp"
            :selected="selected"
            :sunrise="day.sunrise"
            :sunset="day.sunset"
            data-test-id="weather-forecast"
            @select="select"
            @expand-daily="onExpandDaily"
          />
        </div>
      </template>
      <EStatusMessage
        v-else-if="!isLoading"
        class="weather__error"
        data-test-id="weather-error"
        icon="retry"
        stacked
        @click="fetch"
      >
        <template #title>
          {{ $t('common.generic.error') }}
        </template>
        <template #message>
          {{ $t('common.generic.tryagain') }}
        </template>
      </EStatusMessage>
    </div>
  </EInstantAnswer>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { MAIN as MAIN_COOKIE } from '~~/common/js/constants/cookies';
import screen from '~~/common/vue/mixins/screen';
import EInstantAnswer from '~/components/instant-answers/base/legacy';
import ELoading from '~~/common/vue/components/loading';
import EStatusMessage from '~~/common/vue/components/status-message';
import { trackImpression, PAGE_SERP, TYPE_WIDGET } from '~~/search/common/js/client/analytics/rich-content';

import fetch from './api/v2';
import sources from './sources';
import { getTemperatureUnit, FAHRENHEIT, CELSIUS } from '~/components/weather/temperature';
import EForecast from './forecast/index';
import EDetails from './details/index';

const LOADING = 'LOADING';
const ERROR = 'ERROR';
const DONE = 'DONE';

export const HOURLY_FORECAST_LENGTH = 10;

export default {
  name: 'EWeather',
  components: {
    EInstantAnswer,
    ELoading,
    EDetails,
    EStatusMessage,
    EForecast,
  },
  mixins: [screen],
  props: {
    place: {
      type: String,
      required: true,
    },
    region: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      forecast: null,
      initial: null,
      location: null,
      selected: 0,
      state: LOADING,
      units: null,
      showFooter: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    sources() {
      return sources;
    },
    hourlyForecast() {
      const { hourly } = this.forecast[this.selected];
      if (this.selected !== 0) {
        return hourly;
      }

      const now = this.initial.time;
      // only take the forecast items
      // after the current time
      const from = hourly.findIndex(({ time }) => time >= now);
      const fromNow = hourly.slice(from);

      // if there aren't enough items in today's forecast,
      // backfill from tomorrow
      const count = fromNow.length;
      const tomorrow = this.forecast[this.selected + 1];
      if (count < HOURLY_FORECAST_LENGTH && tomorrow) {
        fromNow.push(...tomorrow.hourly.slice(0, HOURLY_FORECAST_LENGTH - count));
      }
      return fromNow;
    },
    day() {
      if (this.selected === 0) {
        return this.initial;
      }
      const day = this.forecast[this.selected];
      return { ...day, temperature: day.maxTemperature };
    },
    details() {
      return {
        description: this.day.description,
        code: this.day.code,
        humidity: this.day.humidity,
        rainAmount: this.day.rainAmount,
        sunrise: this.day.sunrise,
        sunset: this.day.sunset,
        temperature: this.day.temperature,
        time: this.day.time,
        windSpeed: this.day.windSpeed,
      };
    },
    isLoading() {
      return this.state === LOADING;
    },
    contentShown() {
      return this.state !== ERROR && this.location && this.day && this.forecast;
    },
  },
  watch: {
    units(units) {
      this.user.set('temperatureUnit', units === 'metric' ? CELSIUS : FAHRENHEIT);
    },
  },
  mounted() {
    // TODO: refactor this and consolidate EcosiaUser usage across the codebase
    const cookieString = this.$cookies.get(MAIN_COOKIE);
    this.user = new EcosiaUser({ cookieString });
    this.units = getTemperatureUnit(
      this.user.get('temperatureUnit'),
      this.user.get('bingMarketCode'),
    );
    this.fetch();

    trackImpression({
      name: 'weather',
      page: PAGE_SERP,
      type: TYPE_WIDGET,
    });
  },
  methods: {
    async fetch() {
      this.state = LOADING;
      try {
        const { units, place, region, locale } = this;
        const { location, initial, forecast } = await fetch({
          units,
          place,
          region,
          language: locale,
        });
        this.location = location;
        this.forecast = forecast;
        this.initial = initial;
        this.state = DONE;
      } catch (err) {
        this.state = ERROR;
      }
    },
    select(select) {
      this.selected = select;
    },
    setUnits(units) {
      this.units = units;
      this.fetch();
    },
    onExpandDaily() {
      this.showFooter = true;
    },
  },
};
</script>

<style lang="scss" scoped>
$weather-content-height-mobile: 325px;
$weather-content-height-desktop: 385px;

.instant-answer.weather {
  display: flex;
}

.weather {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: space-between;
  flex: 1 0 auto;
  font-size: $font-m;
  position: relative;
  min-height: $weather-content-height-mobile;
  // fixes an issue on Firefox where the entire page would scroll
  // on small desktop screens when the forecast carousel Next button was pressed
  overflow: -moz-hidden-unscrollable;

  @include desktop {
    min-height: $weather-content-height-desktop;
  }
}

.weather--loading,
.weather--error {
  justify-content: center;
}

.weather__error {
  cursor: pointer;
}

.weather__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $z-index-l;
}
</style>
